import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader } from '@/components/ui-kit/PageBits';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Plus, Video, Calendar as CalIcon, ExternalLink, CheckCircle2, LinkIcon } from 'lucide-react';

export default function ClassesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [google, setGoogle] = useState({ configured:false, linked:false });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title:'', description:'', starts_at:'', duration_minutes:60, meet_url:'', group_id:'' });
  const load = () => api.get('/classes').then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get('/google/status').then(r => setGoogle(r.data)).catch(()=>{});
    if (user?.role !== 'student') api.get('/groups').then(r => setGroups(r.data));
  }, [user]);
  const connectGoogle = async () => {
    try {
      const { data } = await api.get('/google/auth');
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Google no está configurado. Añade GOOGLE_CLIENT_ID en el backend .env');
    }
  };
  const save = async () => {
    try {
      await api.post('/classes', { ...f, duration_minutes: Number(f.duration_minutes), group_id: f.group_id || null });
      toast.success('Clase creada');
      setOpen(false); setF({ title:'', description:'', starts_at:'', duration_minutes:60, meet_url:'', group_id:'' }); load();
    } catch { toast.error('Error'); }
  };
  const canCreate = user?.role !== 'student';
  return (
    <div className="p-8 max-w-5xl mx-auto">
      <PageHeader
        title="Clases en vivo"
        subtitle={canCreate ? 'Programa clases con Google Meet' : 'Tus próximas clases'}
        testId="classes-header"
        right={canCreate && (
          <div className="flex items-center gap-2">
            {google.configured && !google.linked && (
              <Button data-testid="google-connect-btn" variant="outline" onClick={connectGoogle} className="rounded-full">
                <LinkIcon className="w-4 h-4 mr-1"/> Conectar Google
              </Button>
            )}
            {google.linked && (
              <span data-testid="google-linked" className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
                <CheckCircle2 className="w-3.5 h-3.5"/> Google conectado
              </span>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-class-btn" className="rounded-full bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1"/>Nueva clase</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Programar clase</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input placeholder="Título" value={f.title} onChange={(e)=>setF({...f,title:e.target.value})}/>
                <Textarea placeholder="Descripción" value={f.description} onChange={(e)=>setF({...f,description:e.target.value})}/>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="datetime-local" value={f.starts_at} onChange={(e)=>setF({...f,starts_at:e.target.value})}/>
                  <Input type="number" placeholder="Minutos" value={f.duration_minutes} onChange={(e)=>setF({...f,duration_minutes:e.target.value})}/>
                </div>
                <Input placeholder="URL de Google Meet (https://meet.google.com/...) — deja vacío para auto-crear si Google está vinculado" value={f.meet_url} onChange={(e)=>setF({...f,meet_url:e.target.value})}/>
                <Select value={f.group_id} onValueChange={(v)=>setF({...f,group_id:v})}>
                  <SelectTrigger><SelectValue placeholder="Grupo"/></SelectTrigger>
                  <SelectContent>{groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                </Select>
                <div className="text-xs text-slate-500">Crea la reunión en <a href="https://meet.google.com/new" target="_blank" rel="noreferrer" className="text-blue-600 underline">meet.google.com/new</a> y pega el enlace aquí.</div>
              </div>
              <DialogFooter><Button data-testid="class-save" onClick={save}>Programar</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      />
      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500">Sin clases programadas.</div>
      ) : (
        <div className="space-y-3">
          {items.map(c => {
            const d = new Date(c.starts_at);
            return (
              <div key={c.id} data-testid={`class-${c.id}`} className="rounded-2xl bg-white border border-slate-200 p-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-blue-100 text-blue-700 grid place-items-center">
                    <CalIcon className="w-6 h-6"/>
                  </div>
                  <div>
                    <div className="font-display font-bold text-lg">{c.title}</div>
                    <div className="text-sm text-slate-500">{d.toLocaleString()} · {c.duration_minutes} min</div>
                  </div>
                </div>
                {c.meet_url ? (
                  <a href={c.meet_url} target="_blank" rel="noreferrer">
                    <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700 border-b-4 border-emerald-800 btn-tactile">
                      <Video className="w-4 h-4 mr-2"/> Entrar a Meet <ExternalLink className="w-3 h-3 ml-2"/>
                    </Button>
                  </a>
                ) : <span className="text-xs text-slate-400">Sin enlace</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
